import {
  ADD_COMPANY_SUCCESS,
  BULK_SAVE_COMPANIES_COMPLETED,
  CANCEL_COMPANY_INLINE_EDIT,
  DELETE_COMPANY_SUCCESS,
  GET_COMPANIES_SUCCESS_UNBATCHED,
  GET_COMPANY_SUCCESS,
  GET_CONTACT_SUCCESS,
  GET_REMINDERS_SUCCESS,
  UPDATE_COMPANY_CUSTOM_FIELDS_SUCCESS,
  UPDATE_COMPANY_PROPERTY,
  UPDATE_COMPANY_SUCCESS,
} from 'actions';
import language from 'config/language';
import { Category, EntityType } from 'enums';
import { ICustomField } from 'interfaces';
import { get as _get, pick as _pick } from 'lodash-es';
import Portfolio from 'models/Portfolio';
import { Model, ModelType, Ref, attr, fk } from 'redux-orm';
import { hasOwnProperty } from 'utils/objects';
import { getBracketByScore } from 'utils/status';

export interface ICompany {
  id: string;
  accountId: string;
  name: string;
  category: Category;
  isArchived: boolean;
  portfolio: Portfolio;
  city: string;
  state: string;
  country: string;
  streetAddress: string;
  postcode: string;
  timestamp: string;
  relationship: number;
  customFields: ICustomField[];
  entityType: EntityType;
  usersRelationship: number;
  websiteUrls: string[];
  timeGroupedInteractionStats?: Record<string, unknown>;
  dirtyFields: {
    [key: string]: any;
  } | null;
}

type CompanyUpsert = Omit<ICompany, 'portfolio'>;

class Company extends Model<typeof Company, ICompany> {
  id!: string;

  accountId!: string;

  name!: string;

  category!: Category;

  isArchived!: boolean;

  portfolio!: Portfolio;

  city!: string;

  state!: string;

  country!: string;

  timestamp!: string;

  relationship!: number;

  customFields!: ICustomField[];

  entityType!: EntityType;

  usersRelationship!: number;

  websiteUrls!: string[];

  timeGroupedInteractionStats?: Record<string, unknown>;

  dirtyFields!: {
    [key: string]: any;
  } | null;

  private cache: Partial<Record<keyof ICompany, any>> = {};

  static reducer(action: any, model: ModelType<Company>) {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case ADD_COMPANY_SUCCESS:
      case GET_COMPANY_SUCCESS:
        model.upsert(action.data);
        break;
      case GET_COMPANIES_SUCCESS_UNBATCHED:
        action.data.forEach((company: CompanyUpsert) => {
          model.upsert(company);
        });
        break;
      case GET_CONTACT_SUCCESS:
        model.upsert(action.data.company);
        break;

      case UPDATE_COMPANY_CUSTOM_FIELDS_SUCCESS:
      case UPDATE_COMPANY_SUCCESS:
        model.upsert(action.data);
        break;

      // These changes are local edits - they have not been saved to the server
      case UPDATE_COMPANY_PROPERTY:
        model.upsert(action.data);
        break;

      // When inline edit is cancelled we need to remove all dirty values
      case BULK_SAVE_COMPANIES_COMPLETED: {
        const edited = model.filter(
          (company: Ref<Company>) => !!company.dirtyFields,
        );
        edited.toModelArray().forEach((company) => {
          company.update({
            ...company.dirtyFields,
            // For custom fields we need to remove the isDirty property
            customFields: company.dirtyFields?.customFields
              ? company.dirtyFields.customFields.map((fields: ICustomField[]) =>
                  _pick(fields, ['key', 'value']),
                )
              : company.customFields || [],
            dirtyFields: null,
          });
        });
        break;
      }
      case CANCEL_COMPANY_INLINE_EDIT: {
        const edited = model.filter(
          (company: Ref<Company>) => !!company.dirtyFields,
        );
        edited.toModelArray().forEach((company) => {
          company.update({
            dirtyFields: null,
          });
        });
        break;
      }
      case DELETE_COMPANY_SUCCESS: {
        // Note we dont use delete() as we want to keep the archived data in the orm store
        // We also archive the companies related contacts and interactions
        const company = model.withId(action.data.id);
        const archive = { isArchived: true };
        if (company) {
          company.update(archive);
          // @ts-ignore - the property is there.
          company.contactSet.update(archive);
        }
        break;
      }
      case GET_REMINDERS_SUCCESS:
        action.data.forEach(
          ({ company }: { company: CompanyUpsert }) =>
            company && model.upsert(company),
        );
        break;
    }

    return undefined;
  }

  toString() {
    return `Company: ${this.name}`;
  }

  /**
   * Used in place of toViewModel for company valueGetters
   * in ContactsView.  Improves performance by selecting
   * only the data required by the getter by key and caches
   * the lookups and transformations.
   *
   * @param key
   * @param defaultVal
   * @returns
   */
  getValue(key: keyof ICompany, defaultVal: unknown): any {
    if (hasOwnProperty(this.cache, key)) {
      return this.cache[key];
    }

    // @ts-ignore - TODO Investigate this.ref[key]
    const val = this[key] || this.ref[key] || defaultVal;

    if (val) {
      this.cache[key] = val;
    }

    return val;
  }

  toViewModel() {
    const status = getBracketByScore(this.relationship || 0);
    const personalStatus = getBracketByScore(this.usersRelationship || 0);

    return {
      ...this.ref,
      customFields: this.customFields || [],
      entityType: EntityType.COMPANY,
      relationship: this.relationship || 0,
      // @ts-ignore not sure how to fix the error on ref TODO
      portfolio: this.portfolio || this.ref.portfolio,
      status: status?.status,
      statusString: status?.label,
      statusObject: status,
      statusObjectPersonal: personalStatus,
      categoryString: _get(
        language.categories.values,
        this.category - 1,
        language.categories.default,
      ).label,
    };
  }
}

Company.modelName = 'Company';

Company.fields = {
  id: attr(),
  accountId: attr(),
  name: attr(),
  category: attr(),
  isArchived: attr(),
  portfolio: fk('Portfolio'),
  city: attr(),
  state: attr(),
  country: attr(),
  timestamp: attr(),
  relationship: attr(),
  customFields: attr(),
  entityType: attr(),
};

export type CompanyViewModel = ReturnType<Company['toViewModel']>;

export default Company;
